import { AutoAwesome, HighlightOff, Send } from "@mui/icons-material"
import { Dialog, DialogContent, DialogTitle, Link } from "@mui/material"
import { KeyboardEvent, useCallback, useState } from "react"
import { intl } from "src/internationalization/intl"
import { Card, Grid, IconButton, Typography, CircularProgress } from "src/lib"
import { TextInput } from "src/lib/components/basics/TextInput"
import { getMarketRankingDisplayName } from "src/parts/CommitmentsSection/useRankingAssets"
import { getResponseFromQuestion, getResponseFromQuestionWithContext } from "src/services/api/chat"
import { setSortingProducts } from "src/services/api/sorting"
import { useConfirmedRankingStore } from "src/store/confirmedRankingStore"
import { useMessageStore } from "src/store/messageStore"
import { useRankingStore } from "src/store/rankingStore"
import { RankingPropertiesPicker } from "./RankingPropertiesPicker"
import { StyledBottom } from "./styled"
import { IChatBottomProps } from "./types"

const BottomArea = (props: IChatBottomProps) => {
    const setActualContext = useMessageStore((s: any) => s.setActualContext)
    const setJsonContext = useMessageStore((s: any) => s.setJsonContext)
    const actualContext = useMessageStore((s: any) => s.actualContext)
    const jsonContext = useMessageStore((s: any) => s.jsonContext)
    const addMessage = useMessageStore((s: any) => s.addMessage)

    const rankings = useRankingStore((s: any) => s.rankings)
    const asset = useRankingStore((s: any) => s.asset)
    const assetKey = useRankingStore((s: any) => s.assetKey)
    const sortingStep = useRankingStore((s: any) => s.sortingStep)
    const sortingContext = useRankingStore((s: any) => s.sortingContext)
    const clearRankings = useRankingStore((s: any) => s.clearRankings)
    const setSortingResponse = useRankingStore((s: any) => s.setSortingResponse)
    const setConfirmedRanking = useConfirmedRankingStore((s: any) => s.setConfirmedRanking)

    const [text, setText] = useState("")
    const [isSending, setIsSending] = useState(false)
    const [propertiesModalOpen, setPropertiesModalOpen] = useState(false)
    const [propertiesPanelOpen, setPropertiesPanelOpen] = useState(false)

    const isRankingEditMode = !!rankings
    const marketKey = assetKey ?? asset ?? ""

    const changeNewMessage = (value: string) => {
        setText(value)
    }

    const insertPromptSnippet = useCallback((snippet: string) => {
        const s = snippet.trim()
        if (!s) return
        setText((prev) => {
            const trimmed = prev.trim()
            return trimmed ? `${trimmed}; ${s}` : s
        })
    }, [])

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey && !!text.trim() && !isSending) {
            e.preventDefault()
            handleClickSend()
        }
    }

    const handleClickSend = async () => {
        if (!text.trim()) return
        const currentText = text
        const context = actualContext
        const jsonContextData = jsonContext

        addMessage({ text: currentText, context, jsonContext, answer: false })
        setText("")
        setIsSending(true)

        try {
            if (isRankingEditMode) {
                const market = assetKey ?? asset
                if (market) {
                    const payload =
                        sortingStep === 1 && sortingContext
                            ? {
                                  market,
                                  step: 1,
                                  user_reply: currentText.trim(),
                                  ranking_text: sortingContext.ranking_text,
                                  cypher_query: sortingContext.cypher_query,
                              }
                            : { market, step: 0, ranking_text: currentText.trim() }
                    const response = await setSortingProducts(payload)
                    const nextStep = response.next_step
                    if (response.next_step === 3 && response.ranking_text) {
                        setConfirmedRanking(market, {
                            ranking_text: response.ranking_text,
                            cypher_query: response.cypher_query,
                        })
                    }
                    setSortingResponse(
                        nextStep === 1 ? 1 : 0,
                        nextStep === 1
                            ? {
                                  ranking_text: response.ranking_text,
                                  cypher_query: response.cypher_query,
                              }
                            : undefined
                    )
                    addMessage({
                        text: response.natural_language_response ?? "Ordenação enviada com sucesso.",
                        context,
                        answer: true,
                        jsonMessage: response,
                        questionText: currentText,
                    })
                }
                return
            }

            if (!context) {
                const responseText = await getResponseFromQuestion(currentText)
                addMessage({
                    text: responseText.natural_language_response,
                    answer: true,
                    jsonMessage: responseText,
                    questionText: currentText,
                })
            } else {
                const responseText = await getResponseFromQuestionWithContext({
                    question: currentText,
                    context: jsonContextData || {},
                })
                addMessage({
                    text: responseText.natural_language_response,
                    jsonContext,
                    context,
                    answer: true,
                    jsonMessage: responseText,
                    questionText: currentText,
                })
            }
        } catch (error) {
            const msg = error instanceof Error ? error.message : String(error)
            const forbidden = msg.includes("403")
            addMessage({
                text: forbidden
                    ? intl("rankingEditForbidden")
                    : intl("chatMessageSendError"),
                context,
                answer: true,
                status: "error",
                questionText: currentText,
            })
        } finally {
            setIsSending(false)
        }
    }

    const handleCloseContextCard = () => {
        setActualContext(undefined)
        setJsonContext(undefined)
        clearRankings()
        setPropertiesModalOpen(false)
        setPropertiesPanelOpen(false)
    }


    const contextLabel = props.context
        ? intl(props.context, { fallback: props.context })
        : ""

    const marketDisplayName = marketKey ? getMarketRankingDisplayName(marketKey) : ""

    return (
        <StyledBottom>
            {!!props.context && (
                <>
                    <Card backgroundColor="white">
                        <Grid container justifyContent="space-between">
                            <Grid>
                                <Grid container alignItems="center" gap={1}>
                                    <AutoAwesome color="primary" />
                                    <Grid>
                                        <Typography color="tertiary" variant="h4">
                                            {intl("contextMessage")}:
                                        </Typography>
                                        <Typography color="tertiary" variant="h4">
                                            {contextLabel}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <IconButton variant="clean" onClick={handleCloseContextCard}>
                                <HighlightOff />
                            </IconButton>
                        </Grid>
                    </Card>
                    {isRankingEditMode && marketKey && (
                        <Grid sx={{ mt: 0.75, pl: 0.5 }}>
                            <Link
                                component="button"
                                type="button"
                                variant="body2"
                                underline="hover"
                                onClick={() => setPropertiesPanelOpen((v) => !v)}
                                sx={{ cursor: "pointer", fontWeight: 600 }}
                            >
                                {propertiesPanelOpen
                                    ? intl("rankingHideProperties")
                                    : intl("rankingViewProperties")}
                            </Link>
                            {propertiesPanelOpen && (
                                <Grid
                                    sx={{
                                        mt: 1,
                                        p: 1,
                                        borderRadius: 1,
                                        border: (t) => `1px solid ${t.palette.divider}`,
                                        bgcolor: "background.paper",
                                    }}
                                >
                                    <RankingPropertiesPicker
                                        marketKey={marketKey}
                                        marketLabel={marketDisplayName}
                                        variant="inline"
                                        onInsertPrompt={insertPromptSnippet}
                                        onOpenModal={() => setPropertiesModalOpen(true)}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    )}
                </>
            )}
            <Dialog
                open={propertiesModalOpen}
                onClose={() => setPropertiesModalOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    <Typography variant="h4">
                        {intl("rankingPropertiesModalTitle", {
                            values: { market: marketDisplayName || marketKey },
                        })}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {marketKey ? (
                        <RankingPropertiesPicker
                            marketKey={marketKey}
                            marketLabel={marketDisplayName}
                            variant="modal"
                            onInsertPrompt={(snippet) => {
                                insertPromptSnippet(snippet)
                                setPropertiesModalOpen(false)
                            }}
                        />
                    ) : (
                        <Typography variant="body2" color="secondary">
                            {intl("rankingPropertiesEmpty")}
                        </Typography>
                    )}
                </DialogContent>
            </Dialog>
            <Grid sx={{ mt: 2, width: "100%" }}>
                <Grid
                    container
                    data-testid="chat-input-area"
                    gap={1}
                    flexGrow={1}
                    justifyContent="center"
                >
                    <Grid item xs={9}>
                        <TextInput
                            autoComplete="off"
                            placeholder={
                                assetKey
                                    ? intl("rankingChatPlaceholder")
                                    : "Digite sua mensagem aqui..."
                            }
                            fullWidth
                            multiline
                            minRows={1}
                            maxRows={6}
                            value={text}
                            onChange={(e) => {
                                changeNewMessage(e.target.value)
                            }}
                            onKeyDown={handleKeyDown}
                            disabled={isSending}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        {isSending ? (
                            <Grid
                                container
                                justifyContent="center"
                                alignItems="center"
                                sx={{ height: "100%" }}
                            >
                                <CircularProgress size={24} color="primary" />
                            </Grid>
                        ) : (
                            <IconButton onClick={handleClickSend} disabled={!text.trim()}>
                                <Send />
                            </IconButton>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </StyledBottom>
    )
}

export { BottomArea }
